import { create } from 'zustand';

import { db } from '@/services/storage/db';
import { WaypointService } from '@/services/waypoint-service';
import { WAYPOINT_TYPE_LABELS } from '@/components/map/map-utils';
import type { Waypoint, CreateWaypointInput, UpdateWaypointInput } from '@/types/waypoint';
import { WaypointType } from '@/types/waypoint';

const waypointService = new WaypointService(db);

interface WaypointStore {
  waypoints: Waypoint[];
  isLoading: boolean;
  error: string | null;
  loadWaypoints: (propertyId: string) => Promise<void>;
  addWaypoint: (data: CreateWaypointInput) => Promise<Waypoint>;
  editWaypoint: (id: string, data: UpdateWaypointInput) => Promise<Waypoint>;
  removeWaypoint: (id: string) => Promise<void>;
  getWaypointsByType: (propertyId: string, type: WaypointType) => Waypoint[];
  getAutoName: (propertyId: string, type: WaypointType) => string;
}

export const useWaypointStore = create<WaypointStore>((set, get) => ({
  waypoints: [],
  isLoading: false,
  error: null,

  loadWaypoints: async (propertyId) => {
    set({ isLoading: true, error: null });
    try {
      const waypoints = await waypointService.getWaypoints(propertyId);
      set({ waypoints, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load waypoints';
      console.error('WaypointStore.loadWaypoints:', err);
      set({ error: message, isLoading: false });
    }
  },

  addWaypoint: async (data) => {
    try {
      const waypoint = await waypointService.createWaypoint(data);
      set((state) => ({ waypoints: [...state.waypoints, waypoint] }));
      return waypoint;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create waypoint';
      console.error('WaypointStore.addWaypoint:', err);
      set({ error: message });
      throw err;
    }
  },

  editWaypoint: async (id, data) => {
    try {
      const updated = await waypointService.updateWaypoint(id, data);
      set((state) => ({
        waypoints: state.waypoints.map((wp) => (wp.id === id ? updated : wp)),
      }));
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update waypoint';
      console.error('WaypointStore.editWaypoint:', err);
      set({ error: message });
      throw err;
    }
  },

  removeWaypoint: async (id) => {
    try {
      await waypointService.deleteWaypoint(id);
      set((state) => ({ waypoints: state.waypoints.filter((wp) => wp.id !== id) }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete waypoint';
      console.error('WaypointStore.removeWaypoint:', err);
      set({ error: message });
      throw err;
    }
  },

  getWaypointsByType: (propertyId, type) => {
    return get().waypoints.filter(
      (wp) => wp.propertyId === propertyId && wp.type === type,
    );
  },

  getAutoName: (propertyId, type) => {
    const count = get().waypoints.filter(
      (wp) => wp.propertyId === propertyId && wp.type === type,
    ).length;
    return `${WAYPOINT_TYPE_LABELS[type]} ${count + 1}`;
  },
}));
